import { React, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Element } from 'react-scroll';
import { useTranslation } from 'react-i18next';

import { ID, CATEGORY, ENTITY, META } from '../../../constants';
import { buildRequest, getStatisticsUrlForHome, getStatisticsImg } from '../../../utils';
import useFetch from '../../Hooks/useFetch';

import Alerts from '../../UI/Alerts/Alerts';
import ContactFooter from '../../UI/ContactFooter/ContactFooter';
import LinksBoxWithName from '../../UI/LinksBoxWithName/LinksBoxWithName';
import LinksBoxesWithName from '../../UI/LinksBoxesWithName/LinksBoxesWithName';
import Loader from '../../UI/Loader/Loader';
import Search from '../../UI/Search/Search';

const HelpAndSupport = () => {
    const { t } = useTranslation();
    const [isMounted, setIsMounted] = useState(false);
    const [alerts, isFetchingAlerts, alertsError] = useFetch(buildRequest(ENTITY.alerts, [CATEGORY.alerts]));
    const [categories, isFetchingCategories, categoriesError] = useFetch(buildRequest(ENTITY.categories, [], { expandEditorDefinedCategories: true }));
    const [statisticsUrl, setStatisticsUrl] = useState('');

    useEffect(() => {
        setIsMounted(true);
        const handleStatistics = () => setStatisticsUrl(getStatisticsUrlForHome());
        handleStatistics();
        return () => setIsMounted(false);
    }, []);

    if (!isMounted) {
        return null;
    }

    const loader = (
        <div className="help-page__loader" data-cy="categories-loader">
            <Loader large spinner />
        </div>
    );

    const helmet = (
        <Helmet>
            <title>{META.homePageTitle}</title>
            <meta name="description" content={META.homePageDescription} />
        </Helmet>
    );

    const isNotAlert = x => x.id !== CATEGORY.alerts;
    const categoriesWithoutAlerts = categories.filter(isNotAlert);

    return (
        <>
            {helmet}
            {statisticsUrl && getStatisticsImg(statisticsUrl)}
            <article className="help-page">
                <header className="help-page__header">
                    <div className="help-page__wrapper">
                        <h1 className="help-page__heading">{t('layout.hs.header')}</h1>
                        <Search />
                    </div>
                </header>

                <div className="help-page__content constrain" data-cy="help-page-content">
                    {categoriesError ?
                        <p style={{ 'textAlign': 'center' }}>
                            <b>{t('layout.hs.categories.error')}</b>
                        </p>
                        : <>
                            {isFetchingAlerts || isFetchingCategories
                                ? loader
                                : <>
                                    {!alertsError && alerts?.length > 0 && (
                                        <div className="help-page__alerts" data-cy="home-alerts">
                                            <Alerts alerts={alerts?.slice(0, 2)} isFetchingAlerts={isFetchingAlerts} />
                                        </div>
                                    )}

                                    <Element name={ID.homePageCategoriesId} id={ID.homePageCategoriesId} data-cy="categories-section">
                                        <div className="help-page__subheader">
                                            <h2 className="help-page__headline" data-cy="categories-section-header">
                                                {t('layout.hs.categories.header')}
                                            </h2>
                                        </div>
                                        <div className="help-page__grid" data-cy="categories-section-list">
                                            <div className="help-page__grid-col help-page__grid-col--wide">
                                                <LinksBoxesWithName
                                                    isFetching={isFetchingCategories}
                                                    links={categories} />
                                            </div>
                                            <div className="help-page__grid-col help-page__grid-col--narrow">
                                                <LinksBoxWithName
                                                    isFetching={isFetchingCategories}
                                                    links={categoriesWithoutAlerts}
                                                    boxName={t('layout.hs.categories.more')} />
                                            </div>
                                        </div>
                                    </Element>
                                </>
                            }
                        </>
                    }
                </div>
            </article>
            <ContactFooter />
        </>
    );
};

export default HelpAndSupport;
